import type AppRouter from '@omniwound/api';
import { useAuth } from '@platform/keycloak-react-auth';
import {
  createTRPCClient,
  httpBatchLink,
  httpLink,
  splitLink,
} from '@trpc/client';
import { useEffect, useMemo, useRef } from 'react';

import env from '@/config/env';
import { authRecoveryLink } from '@/lib/auth-recovery-link';
import { registerBffAuthHeaders } from '@/lib/bff-content-request';
import {
  cancelTentativeAuthRedirect,
  completeAuthRecoveryAfterBffSuccess,
  markAuthRecoveryDocumentVerifying,
  markAuthRedirectStarted,
} from '@/lib/auth-redirect';
import {
  apiErrorDomainCode,
  AUTH_INFRASTRUCTURE_UNAVAILABLE,
} from '@/lib/api-error';
import {
  authorizationIntentHeaders,
  carriesAuthorizationIntent,
} from '@/lib/authorization-intent';
import { captureDeepLink } from '@/lib/deep-link';
import { errorRecoveryLink } from '@/lib/error-recovery-link';
import { useAuthRecoveryStore } from '@/stores';

type AuthRecovery = {
  authUnavailable: boolean;
  trpcClient: ReturnType<typeof createTRPCClient<AppRouter>>;
};

export function useAuthRecovery(): AuthRecovery {
  const { getToken, getValidToken, isTokenExpired, login, updateToken } =
    useAuth();
  const authUnavailable = useAuthRecoveryStore(
    (state) => state.authUnavailable,
  );
  const setAuthUnavailable = useAuthRecoveryStore(
    (state) => state.setAuthUnavailable,
  );
  const getTokenRef = useRef(getToken);
  const getValidTokenRef = useRef(getValidToken);
  const isTokenExpiredRef = useRef(isTokenExpired);
  const loginRef = useRef(login);
  const updateTokenRef = useRef(updateToken);

  useEffect(() => {
    getTokenRef.current = getToken;
    getValidTokenRef.current = getValidToken;
    loginRef.current = login;
    updateTokenRef.current = updateToken;
    isTokenExpiredRef.current = isTokenExpired;
  }, [getToken, getValidToken, isTokenExpired, login, updateToken]);

  const trpcClient = useMemo(() => {
    markAuthRecoveryDocumentVerifying();

    const getAuthHeaders = async (): Promise<Record<string, string>> => {
      const token = await getValidTokenRef.current();
      if (!token) {
        return {};
      }
      return { Authorization: `Bearer ${token}` };
    };
    // The BFF's REST content routes (signed PDFs) authenticate exactly as its
    // procedures do.
    registerBffAuthHeaders(getAuthHeaders);

    let recoveryPromise: Promise<void> | undefined;

    const recoverAfterUnauthorized = async (): Promise<void> => {
      if (!markAuthRedirectStarted()) {
        return;
      }
      // A mid-session 401 re-login round-trips through Keycloak the same way a
      // reload does, so snapshot the current deep link before it is lost.
      captureDeepLink();

      try {
        // A forced refresh distinguishes a rejected BFF token from an identity
        // infrastructure outage without relying on the token's local expiry.
        await updateTokenRef.current(-1);
      } catch {
        const token = getTokenRef.current();
        if (!token || isTokenExpiredRef.current()) {
          // keycloak-js does not guarantee that a failed refresh starts a
          // login redirect. Expired or cleared authentication follows the
          // normal login-required recovery path exactly once.
          await loginRef.current();
          return;
        }
        // A transport failure leaves keycloak-js's non-expired token intact.
        // No redirect occurred, so the tentative marker can be removed before
        // degrading.
        cancelTentativeAuthRedirect();
        setAuthUnavailable(true);
        return;
      }

      await loginRef.current();
    };

    const requestLoginAfterUnauthorized = (error: unknown): Promise<void> => {
      if (apiErrorDomainCode(error) === AUTH_INFRASTRUCTURE_UNAVAILABLE) {
        cancelTentativeAuthRedirect();
        setAuthUnavailable(true);
        return Promise.resolve();
      }

      if (!recoveryPromise) {
        const recovery = recoverAfterUnauthorized();
        recoveryPromise = recovery.finally(() => {
          recoveryPromise = undefined;
        });
      }
      return recoveryPromise;
    };

    return createTRPCClient<AppRouter>({
      links: [
        authRecoveryLink({
          onUnauthorized: requestLoginAfterUnauthorized,
          onBffSuccess: completeAuthRecoveryAfterBffSuccess,
        }),
        // Must precede httpBatchLink so it sees the response on the way back up
        // and can convert BFF error envelopes into thrown TRPCClientErrors.
        errorRecoveryLink(),
        // An operation carrying an authorization intent leaves the batch. One
        // HTTP POST can hold several procedures, so a request-level header
        // cannot name a single action; an unbatched request can. Reads keep
        // batching because the BFF generates their intent at ingress.
        splitLink({
          condition: carriesAuthorizationIntent,
          true: httpLink({
            url: env.bffBaseUrl,
            headers: async ({ op }) => ({
              ...(await getAuthHeaders()),
              ...authorizationIntentHeaders(op),
            }),
          }),
          false: httpBatchLink({
            url: env.bffBaseUrl,
            headers: getAuthHeaders,
          }),
        }),
      ],
    });
  }, [setAuthUnavailable]);

  return { authUnavailable, trpcClient };
}
